/**
 * 预置音色**单源**(2026-08-07 R3-a)。
 *
 * ★为什么要这个 hook:写死的 `DH_VOICE_OPTIONS` 与后端 `preset-voices` 是两个源,
 *   两边一漂就出「选不到 / 回显成裸 id」。创建页 2026-07 已经因此坏过一次(声音库带过来的
 *   presetVoice 在创建页 Select 里没有对应 SelectItem,音色框渲染成空白,用户以为没选上),
 *   当时只就地修了创建页,详情页 / 超管上架 / 企微 H5 三处没跟 —— 同一个洞留了四处。
 *
 * ★实查(2026-08-07,本地 dev 指测试机,真下拉数选项):后端当前返回 **8 款,与写死表逐字一致**。
 *   所以这轮收敛是**防漂**,不是「今天有人选不到音色」——别拿一个当前不成立的后果去论证紧急度。
 *
 * 回落规则不变:后端不可达 / 未 seed → 用写死表,选择器不塌(存量兜底一个字不改)。
 */
import { useQuery } from "@tanstack/react-query";

import { digitalHumanService, type PresetVoiceDto } from "../api/digitalHumanService";
import { DH_VOICE_OPTIONS } from "./dhCreateOptions";

export interface PresetVoiceOption {
  value: string;
  label: string;
}

export function usePresetVoiceOptions(): {
  options: PresetVoiceOption[];
  /** 后端原始条目(带 usage / sampleUrl)——配音页的试听墙与用途筛要用。 */
  voices: PresetVoiceDto[];
  /** 音色 id → 展示名;查不到就回 id 本身(不编造名字)。 */
  labelOf: (id: string | null | undefined) => string;
} {
  const q = useQuery({
    queryKey: ["dh", "preset-voices"],
    queryFn: () => digitalHumanService.listPresetVoices(),
    staleTime: 5 * 60_000,
    retry: false,
    // ★退订全局错误 toast:这条查询**有回落**(拿不到就用写死表,选择器照样能用),
    //   再弹一个「加载失败,请稍后重试」是在为一件没坏的事报警。企微 H5 那侧的
    //   QueryCache.onError 默认会弹,所以必须显式退订(advisorMain.tsx 的既有机制)。
    meta: { suppressGlobalErrorToast: true },
  });

  const options: PresetVoiceOption[] = (q.data ?? []).length
    ? q.data!.map((v) => ({ value: v.id, label: v.label }))
    : DH_VOICE_OPTIONS;

  const labelOf = (id: string | null | undefined): string =>
    (id ? options.find((o) => o.value === id)?.label : undefined) ?? id ?? "";

  return { options, voices: q.data ?? [], labelOf };
}
